import { THEME_MODE_KEY, DARK_THEME, LIGHT_THEME } from './security/themeInitScript';
import { readStoredValue, writeStoredValue } from './storage';

export type ThemeMode = 'SYSTEM' | 'LIGHT' | 'DARK';

/** 현재 OS 테마가 dark인지 여부. matchMedia가 없으면 dark로 간주합니다. */
const prefersDarkScheme = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/** 테마 모드를 실제 `data-theme`/`color-scheme`로 반영합니다. SYSTEM은 OS 설정을 따릅니다. */
export const applyTheme = (mode: ThemeMode) => {
  if (typeof window === 'undefined') return;
  let theme: string;
  if (mode === 'DARK') {
    theme = DARK_THEME;
  } else if (mode === 'LIGHT') {
    theme = LIGHT_THEME;
  } else {
    theme = prefersDarkScheme() ? DARK_THEME : LIGHT_THEME;
  }
  const d = document.documentElement;
  d.setAttribute('data-theme', theme);
  d.style.colorScheme = theme === DARK_THEME ? 'dark' : 'light';
  d.style.backgroundColor = theme === DARK_THEME ? '#1d232a' : '#ffffff';
};

/** 저장된 테마 모드를 반환합니다. 명시 선택(LIGHT/DARK)이 없으면 SYSTEM입니다. */
export const getStoredThemeMode = (): ThemeMode => {
  // 저장소 차단 환경에서는 OS 설정을 따른다 (WEB-R).
  const stored = readStoredValue(THEME_MODE_KEY);
  if (stored === 'LIGHT' || stored === 'DARK') return stored;
  return 'SYSTEM';
};

/** 테마 모드를 저장하고 즉시 반영합니다. */
export const saveThemeMode = (mode: ThemeMode) => {
  writeStoredValue(THEME_MODE_KEY, mode);
  applyTheme(mode);
};

/**
 * OS의 `prefers-color-scheme` 변경을 구독합니다.
 * 구형 Safari의 `addListener` 폴백을 함께 처리하고, 구독 해제 함수를 반환합니다.
 */
export const subscribeSystemThemeChange = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  type LegacyMediaQueryList = MediaQueryList & {
    addListener?: (fn: () => void) => void;
    removeListener?: (fn: () => void) => void;
  };
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }
  const legacy = media as LegacyMediaQueryList;
  if (typeof legacy.addListener === 'function') {
    legacy.addListener(handler);
    return () => legacy.removeListener?.(handler);
  }
  return () => {};
};
